/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Help & About pane. Three sections:
//   * Contact support — a message form that composes a prefilled email to
//     support (mailto:), so it works with no backend support endpoint.
//   * Guides — quick links to the docs (opened in the browser / default app).
//   * FAQs — an expandable accordion of common questions.
//   * About — app version + legal links.

import { ChevronDown, ExternalLink, LifeBuoy } from "lucide-react-native";
import { useCallback, useState } from "react";
import { Linking, Pressable, Text, TextInput, View } from "react-native";
import { WEB_INPUT_OUTLINE_RESET } from "../../foundations/webInputStyle";
import { PillButton, TextField } from "../controls";
import { SettingsDivider, SettingsRow } from "../SettingsRow";

const SUPPORT_EMAIL = "hello@ping.live";
const DOCS_BASE = "https://ping.live";

export interface HelpAboutPaneProps {
	/** Shown in the About section. Defaults to a generic label. */
	readonly appVersion?: string;
	/** Replay the first-run app tour (chat, calls, contacts, tools). Omit to hide. */
	readonly onReplayHomeTour?: () => void;
	/** Replay the settings walkthrough. Omit to hide. */
	readonly onReplaySettingsTour?: () => void;
}

interface Guide {
	readonly label: string;
	readonly description: string;
	readonly url: string;
}

const GUIDES: readonly Guide[] = [
	{
		label: "Getting started",
		description: "Set up your profile, add contacts, and start chatting.",
		url: `${DOCS_BASE}/guides/getting-started`,
	},
	{
		label: "Security & encryption",
		description: "How Ping keeps every message end-to-end encrypted with MLS.",
		url: `${DOCS_BASE}/guides/security`,
	},
	{
		label: "Linking & managing devices",
		description: "Add a new device, review your sessions, and sign out remotely.",
		url: `${DOCS_BASE}/guides/devices`,
	},
	{
		label: "Backup & recovery",
		description: "Save your recovery passphrase so you never lose your account.",
		url: `${DOCS_BASE}/guides/recovery`,
	},
];

interface Faq {
	readonly q: string;
	readonly a: string;
}

const FAQS: readonly Faq[] = [
	{
		q: "Is my conversation really end-to-end encrypted?",
		a: "Yes. Every conversation uses the MLS protocol (RFC 9420). Keys never leave your devices, so Ping's servers can't read your messages.",
	},
	{
		q: "How do I add another device?",
		a: "Open Settings → Linked devices and follow the pairing flow. Your existing device approves the new one, then your chats sync automatically.",
	},
	{
		q: "What happens if I lose my device?",
		a: "Back up your recovery passphrase in Settings → Account. On a new device you can restore your account with it. Without a backup, your message history can't be recovered.",
	},
	{
		q: "How do I know a contact is verified?",
		a: "Verified contacts show a badge next to their name. You can also compare safety numbers in a chat's info panel.",
	},
	{
		q: "Can I use Ping on multiple devices at once?",
		a: "Yes. Each device is its own encryption leaf, so messages are delivered to every device you've linked.",
	},
];

export function HelpAboutPane({
	appVersion,
	onReplayHomeTour,
	onReplaySettingsTour,
}: HelpAboutPaneProps): JSX.Element {
	const [message, setMessage] = useState("");
	const [replyTo, setReplyTo] = useState("");
	const [openFaq, setOpenFaq] = useState<number | null>(null);

	const openUrl = useCallback((url: string) => {
		void Linking.openURL(url).catch(() => {
			/* No handler available — nothing else to do. */
		});
	}, []);

	const sendSupport = useCallback(() => {
		const trimmed = message.trim();
		if (trimmed.length === 0) return;
		const subject = encodeURIComponent("Ping support request");
		const bodyLines = [trimmed, "", "—", replyTo.trim().length > 0 ? `Reply to: ${replyTo.trim()}` : ""];
		const body = encodeURIComponent(bodyLines.filter(Boolean).join("\n"));
		openUrl(`mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`);
	}, [message, replyTo, openUrl]);

	return (
		<View className="flex flex-col gap-4">
			<SettingsRow
				variant="hero"
				testID="settings-helpAbout-intro"
				title="Help & About"
				description="Get help, browse guides, or reach the Ping team."
			/>

			{/* Contact support */}
			<SettingsRow
				testID="settings-helpAbout-support"
				title="Contact support"
				description={`Tell us what's going on and we'll get back to you at ${SUPPORT_EMAIL}.`}
			>
				<View className="max-w-[475px] gap-3">
					<View className="gap-1 w-full">
						<Text className="text-[12px] leading-[16px] font-normal text-light-text-secondary dark:text-[#B5BAC3]">
							Message
						</Text>
						<TextInput
							value={message}
							onChangeText={setMessage}
							placeholder="Describe your question or issue…"
							placeholderTextColor="#6B7280"
							multiline
							numberOfLines={5}
							textAlignVertical="top"
							maxLength={2000}
							className="w-full min-h-[104px] px-3 py-2 rounded-[8px] bg-light-surface-secondary dark:bg-[#111318] font-sans text-[14px] leading-[20px] text-light-text-primary dark:text-white"
							style={WEB_INPUT_OUTLINE_RESET}
						/>
					</View>
					<TextField
						label="Your email (optional)"
						value={replyTo}
						onChange={setReplyTo}
						placeholder="So we can reply"
						type="email"
						autoComplete="email"
					/>
					<View className="flex flex-row items-center gap-3">
						<PillButton
							variant="primary"
							onClick={sendSupport}
							disabled={message.trim().length === 0}
						>
							Send message
						</PillButton>
						<PillButton variant="ghost" onClick={() => openUrl(`mailto:${SUPPORT_EMAIL}`)}>
							Email us directly
						</PillButton>
					</View>
				</View>
			</SettingsRow>

			<SettingsDivider />

			{/* Guides */}
			<SettingsRow testID="settings-helpAbout-guides" title="Guides" description="Step-by-step help for the essentials.">
				<View className="flex flex-col gap-2 max-w-[560px]">
					{GUIDES.map((g) => (
						<Pressable
							key={g.url}
							accessibilityRole="link"
							accessibilityLabel={g.label}
							onPress={() => openUrl(g.url)}
							className="flex flex-row items-center gap-3 px-3 py-2.5 rounded-lg bg-light-surface-primary dark:bg-[#11131A] border border-light-border-secondary dark:border-[#22242A] hover:bg-light-surface-secondary dark:hover:bg-[#15171C]"
						>
							<View className="w-9 h-9 rounded-lg bg-light-surface-secondary dark:bg-[#15171C] flex items-center justify-center">
								<LifeBuoy size={18} strokeWidth={1.75} color="#537EFF" />
							</View>
							<View className="min-w-0 flex-1">
								<Text className="text-[14px] leading-[20px] font-semibold text-light-text-primary dark:text-[#F5F6F8]">
									{g.label}
								</Text>
								<Text
									numberOfLines={1}
									className="text-[12px] leading-[16px] text-light-text-secondary dark:text-[#B5BAC3]"
								>
									{g.description}
								</Text>
							</View>
							<ExternalLink size={15} strokeWidth={1.75} color="#8C929E" />
						</Pressable>
					))}
				</View>
			</SettingsRow>

			<SettingsDivider />

			{/* Guided tours — replay the onboarding walkthroughs on demand. */}
			{(onReplayHomeTour !== undefined || onReplaySettingsTour !== undefined) && (
				<>
					<SettingsRow
						testID="settings-helpAbout-tours"
						title="Guided tours"
						description="New here, or just need a refresher? Walk through Ping again."
					>
						<View className="flex flex-row items-center gap-3 flex-wrap">
							{onReplayHomeTour !== undefined && (
								<PillButton variant="secondary" onClick={onReplayHomeTour}>
									Replay app tour
								</PillButton>
							)}
							{onReplaySettingsTour !== undefined && (
								<PillButton variant="secondary" onClick={onReplaySettingsTour}>
									Replay settings tour
								</PillButton>
							)}
						</View>
					</SettingsRow>

					<SettingsDivider />
				</>
			)}

			{/* FAQs */}
			<SettingsRow testID="settings-helpAbout-faqs" title="FAQs" description="Answers to the questions we hear most.">
				<View className="flex flex-col gap-2 max-w-[560px]">
					{FAQS.map((f, i) => {
						const open = openFaq === i;
						return (
							<View
								key={f.q}
								className="rounded-lg bg-light-surface-primary dark:bg-[#11131A] border border-light-border-secondary dark:border-[#22242A] overflow-hidden"
							>
								<Pressable
									accessibilityRole="button"
									accessibilityState={{ expanded: open }}
									onPress={() => setOpenFaq(open ? null : i)}
									className="flex flex-row items-center gap-3 px-3 py-3"
								>
									<Text className="flex-1 text-[14px] leading-[20px] font-semibold text-light-text-primary dark:text-[#F5F6F8]">
										{f.q}
									</Text>
									<View className={open ? "rotate-180" : "rotate-0"}>
										<ChevronDown size={16} strokeWidth={2} color="#8C929E" />
									</View>
								</Pressable>
								{open && (
									<Text className="px-3 pb-3 text-[13px] leading-[19px] text-light-text-secondary dark:text-[#B5BAC3]">
										{f.a}
									</Text>
								)}
							</View>
						);
					})}
				</View>
			</SettingsRow>

			<SettingsDivider />

			{/* About */}
			<SettingsRow testID="settings-helpAbout-about" title="About Ping" description="End-to-end encrypted with the MLS protocol (RFC 9420).">
				<View className="flex flex-col gap-2">
					<Text className="text-[13px] leading-[18px] text-light-text-secondary dark:text-[#B5BAC3]">
						Version {appVersion ?? "Ping for Web"}
					</Text>
					<View className="flex flex-row items-center gap-3">
						<PillButton variant="secondary" onClick={() => openUrl(`${DOCS_BASE}/privacy`)}>
							Privacy Policy
						</PillButton>
						<PillButton variant="secondary" onClick={() => openUrl(`${DOCS_BASE}/terms`)}>
							Terms of Service
						</PillButton>
					</View>
				</View>
			</SettingsRow>
		</View>
	);
}
